<template>
    <div
        class="list-header"
        :class="{
            'list-header--without-margin': withoutMargin,
        }"
    >
        <div class="list-header__titles">
            <h1 class="list-header__title">
                <span>{{ title }}</span>
                <span v-if="$slots['info']" class="list-header__tooltip">
                    <Tooltip with-hover>
                        <template v-slot:content>
                            <InfoIcon class="list-header__tooltip-icon" />
                        </template>
                        <template v-slot:tooltip>
                            <slot name="info" class="list-header__tooltip-info" />
                        </template>
                    </Tooltip>
                </span>
            </h1>
            <div v-if="$slots['subtitle']" class="list-header__subtitle">
                <slot name="subtitle" />
            </div>
        </div>

        <div v-if="$slots['action']" class="list-header__action">
            <slot name="action" />
        </div>
        <div v-if="$slots['counts']" class="list-header__counts">
            <slot name="counts" />
        </div>
        <div v-else-if="totalCount" class="list-header__counts">
            <div class="list-header__counts-item" :class="{ [`list-header__counts-item--${countAlign}`]: true }">
                <div v-if="$slots['prev-count']" class="list-header__prev-count">
                    <slot name="prev-count" />
                </div>
                <div class="list-header__counts-item-number">
                    {{ totalCount }}
                </div>
                <div class="list-header__counts-item-text">
                    {{ titleTotalCount }}
                </div>
            </div>
        </div>
        <div v-if="totalCount === 0" class="list-header__counts-item">
            <div v-if="$slots['count-default']" class="list-header__prev-count">
                <slot name="count-default" />
            </div>
        </div>
        <div v-if="isMobile && !hideDivider" class="list-header__mobile-divider"></div>
        <div v-if="$slots['subaction']" class="list-header__subaction">
            <slot name="subaction" />
        </div>
    </div>
</template>

<script>
import Tooltip from '@/components/UI/Tooltip.vue';
import InfoIcon from '@/components/UI/icons/InfoIcon.vue';
import { hide } from '@popperjs/core';
import { mapGetters } from 'vuex';

const getters = ['isMobile'];

export default {
    name: 'ListHeader',
    components: { InfoIcon, Tooltip },
    props: {
        title: {
            type: String,
            require: true,
        },
        hideDivider: {
            type: Boolean,
            default: false,
        },
        totalCount: {
            type: [Number, String],
            default: () => 0,
        },
        titleTotalCount: {
            type: [Number, String],
            default: () => 0,
        },
        countAlign: {
            type: String,
            validate: (v) => ['left', 'center', 'right'].includes(v),
            default: () => 'right',
        },
        withoutMargin: {
            type: Boolean,
            default: () => false,
        },
    },
    computed: {
        ...mapGetters(getters),
    },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/rem';
@import '~@/styles/mrem';
@import '~@/styles/variables';
@import '~@/styles/buttons_v2';

.list-header {
    color: $color-secondary-dark_violet;
    font-family: $font-montserrat;
    position: relative;

    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-direction: column;
    position: relative;
    gap: urem(16);

    @include on-desktop {
        align-items: center;
        flex-direction: row;
        margin-bottom: rem(80);
        border-bottom: urem(2.5) solid #848196;
        padding-bottom: urem(16);

        &--without-margin {
            margin-bottom: urem(40);
        }
    }

    &__mobile-divider {
        width: 100%;
        border-bottom: urem(2.5) solid #848196;
        margin-bottom: urem(16);
    }

    &__title {
        position: relative;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        align-content: flex-start;
        gap: urem(6);
        margin: 0;
        font-size: mrem(32);
        font-weight: 700;
        line-height: mrem(40);
        text-align: left;
        color: $color-secondary-dark_violet;

        @include on-desktop {
            font-family: $font-montserrat;
            font-style: normal;
            font-size: rem(56);
            font-weight: 700;
            line-height: rem(66);
            text-align: left;
        }

        :deep(.tooltip) {
            top: calc(100% + #{urem(10)}) !important;
            left: 0 !important;
            display: inline-block;
            vertical-align: top;
            max-width: urem(1260);
            width: max-content;
            transform: none !important;

            @include on-mobile {
                max-width: calc(100vw - #{urem(31)});
            }
        }
    }

    &__tooltip {
        line-height: urem(16.67);

        @include on-desktop {
            line-height: urem(24);
        }
    }

    &__tooltip-icon {
        width: urem(16.67);
        height: urem(16.67);

        @include on-desktop {
            width: urem(24);
            height: urem(24);
        }
    }

    &__counts {
        text-align: center;
        display: flex;

        @include on-desktop {
            position: absolute;
            top: rem(-12);
            right: 0;
        }

        &-item {
            display: flex;
            flex-direction: row;
            justify-content: center;
            align-items: center;
            margin-right: mrem(24);
            gap: urem(6);

            @include on-desktop {
                flex-direction: column;
                margin-right: 0;
                margin-left: rem(67);
                gap: 0;

                &--right {
                    align-items: flex-end;
                }
                &--left {
                    align-items: flex-start;
                }
                &--center {
                    align-items: center;
                }
            }

            &-number {
                @include set-font-size(24, 800, 34);

                @include on-desktop {
                    @include set-font-size(56, 800, 66);
                    width: 100%;
                    text-align: center;
                }
            }

            &-text {
                @include set-font-size(16, 400, 34);
                text-transform: lowercase;

                @include on-desktop {
                    @include set-font-size(16, 400, 18);
                    text-align: center;
                    width: 100%;
                }
            }
        }
    }

    &__action {
        width: 100%;
    }

    &__subaction {
        width: 100%;

        @include on-desktop {
            border: none;
            padding: 0;
        }
    }
}
</style>
